/**
 * URS-F345 — Delegation Log Panel
 *
 * Pestaña dentro del estudio para gestionar el registro de delegación de
 * funciones (ICH-GCP / 21 CFR 312). Solo PI o Director del estudio (o
 * admins) pueden crear/editar/firmar. Una vez firmado, el log es de solo
 * lectura: para modificarlo hay que crear una nueva versión.
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import ESignature from '../ESignature';
import { Pencil, Trash, Plus, FileText, CheckCircle, ArrowClockwise } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DelegationLogPanel({ studyId, studyCode, lang = 'es' }) {
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState(null);
  const [history, setHistory] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [entryDialog, setEntryDialog] = useState(null); // {mode:'create'|'edit', entry?}
  const [showSign, setShowSign] = useState(false);
  const [showESignDialog, setShowESignDialog] = useState(false);
  const [effectiveTo, setEffectiveTo] = useState('');

  const lb = lang === 'es' ? {
    title: 'Delegation Log',
    subtitle: 'Registro de delegación de funciones (ICH-GCP / 21 CFR 312)',
    no_log: 'Este estudio aún no tiene un Delegation Log',
    create_log: 'Crear Delegation Log',
    new_version: 'Nueva versión',
    download_pdf: 'Descargar PDF',
    version: 'Versión',
    status_draft: 'BORRADOR',
    status_active: 'FIRMADO Y VIGENTE',
    status_superseded: 'OBSOLETO',
    effective_from: 'Vigente desde',
    effective_to: 'Vigente hasta',
    activated_by: 'Firmado por',
    activated_at: 'Firmado el',
    add_entry: 'Añadir persona',
    no_entries: 'Aún no hay personas delegadas. Pulsa "Añadir persona".',
    th_name: 'Nombre',
    th_role: 'Rol / título',
    th_qual: 'Cualificaciones',
    th_tasks: 'Tareas delegadas',
    th_from: 'Desde',
    th_to: 'Hasta',
    th_actions: 'Acciones',
    edit: 'Editar',
    delete: 'Eliminar',
    confirm_delete: '¿Eliminar esta delegación?',
    sign_btn: 'Firmar y activar',
    sign_dialog_title: 'Firmar Delegation Log',
    sign_note: 'Al firmar quedará activo y de solo lectura. Para modificarlo deberás crear una nueva versión.',
    f_name: 'Nombre completo',
    f_user: 'Usuario del sistema (opcional)',
    f_role: 'Rol / título profesional',
    f_qual: 'Cualificaciones (titulación, especialidad...)',
    f_tasks: 'Tareas delegadas',
    f_from: 'Fecha de inicio de la delegación',
    f_to: 'Fecha fin (opcional)',
    save: 'Guardar',
    cancel: 'Cancelar',
    history_title: 'Historial de versiones anteriores',
    locked: 'Este log está firmado y es de solo lectura',
    no_entries_to_sign: 'Añade al menos una persona antes de firmar',
    success_signed: 'Delegation Log firmado y activado',
    err_required_name: 'El nombre es obligatorio',
    err_required_tasks: 'Selecciona al menos una tarea',
  } : {
    title: 'Delegation Log',
    subtitle: 'Task delegation log (ICH-GCP / 21 CFR 312)',
    no_log: 'This study has no Delegation Log yet',
    create_log: 'Create Delegation Log',
    new_version: 'New version',
    download_pdf: 'Download PDF',
    version: 'Version',
    status_draft: 'DRAFT',
    status_active: 'SIGNED & ACTIVE',
    status_superseded: 'SUPERSEDED',
    effective_from: 'Effective from',
    effective_to: 'Effective to',
    activated_by: 'Signed by',
    activated_at: 'Signed at',
    add_entry: 'Add person',
    no_entries: 'No entries yet. Click "Add person".',
    th_name: 'Name',
    th_role: 'Role / title',
    th_qual: 'Qualifications',
    th_tasks: 'Delegated tasks',
    th_from: 'From',
    th_to: 'To',
    th_actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    confirm_delete: 'Delete this delegation?',
    sign_btn: 'Sign & activate',
    sign_dialog_title: 'Sign Delegation Log',
    sign_note: 'After signing, the log becomes read-only. To modify it you will need to create a new version.',
    f_name: 'Full name',
    f_user: 'System user (optional)',
    f_role: 'Role / professional title',
    f_qual: 'Qualifications (degree, specialty...)',
    f_tasks: 'Delegated tasks',
    f_from: 'Delegation start date',
    f_to: 'End date (optional)',
    save: 'Save',
    cancel: 'Cancel',
    history_title: 'Previous versions',
    locked: 'This log is signed and read-only',
    no_entries_to_sign: 'Add at least one person before signing',
    success_signed: 'Delegation Log signed & activated',
    err_required_name: 'Name is required',
    err_required_tasks: 'Select at least one task',
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [logResp, tasksResp, usersResp] = await Promise.all([
        axios.get(`${API}/api/studies/${studyId}/delegation-log`, { withCredentials: true }),
        axios.get(`${API}/api/delegation-tasks`, { withCredentials: true }),
        axios.get(`${API}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLog(logResp.data.current);
      setHistory(logResp.data.history || []);
      setTasks(tasksResp.data || []);
      setUsers(usersResp.data || []);
    } catch (e) {
      toast.error('Error cargando Delegation Log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (studyId) loadAll(); /* eslint-disable-next-line */ }, [studyId]);

  const isLocked = log && log.status === 'active';

  const handleCreate = async () => {
    try {
      const r = await axios.post(`${API}/api/studies/${studyId}/delegation-log`, {}, { withCredentials: true });
      setLog(r.data);
      toast.success(lang === 'es' ? 'Borrador creado' : 'Draft created');
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(lb.confirm_delete)) return;
    try {
      await axios.delete(`${API}/api/studies/${studyId}/delegation-log/entries/${entry.entry_id}`, { withCredentials: true });
      await loadAll();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleSign = async (signature) => {
    setShowESignDialog(false);
    try {
      await axios.post(`${API}/api/studies/${studyId}/delegation-log/sign`,
        { signature, effective_to: effectiveTo || null },
        { withCredentials: true });
      toast.success(lb.success_signed);
      await loadAll();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleDownloadPdf = (delegationId) => {
    const url = `${API}/api/studies/${studyId}/delegation-log/${delegationId}/pdf`;
    window.open(url, '_blank');
  };

  if (loading) {
    return <div className="p-8 text-center text-stone-500">{lang === 'es' ? 'Cargando…' : 'Loading…'}</div>;
  }

  return (
    <div className="space-y-6" data-testid="delegation-log-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#1f3e80]">{lb.title}</h2>
          <p className="text-sm text-stone-500 mt-0.5">{lb.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!log && (
            <Button data-testid="dl-create-btn" onClick={handleCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2">
              <Plus weight="bold" size={14} /> {lb.create_log}
            </Button>
          )}
          {log && log.status === 'draft' && (
            <Button data-testid="dl-sign-btn" onClick={() => {
              if (!log.entries || log.entries.length === 0) { toast.error(lb.no_entries_to_sign); return; }
              setShowSign(true);
            }} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2">
              <CheckCircle weight="bold" size={14} /> {lb.sign_btn}
            </Button>
          )}
          {log && log.status === 'active' && (
            <>
              <Button data-testid="dl-newver-btn" onClick={handleCreate} variant="outline" className="gap-2">
                <ArrowClockwise weight="bold" size={14} /> {lb.new_version}
              </Button>
              <Button data-testid="dl-pdf-btn" onClick={() => handleDownloadPdf(log.delegation_id)} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2">
                <FileText weight="bold" size={14} /> {lb.download_pdf}
              </Button>
            </>
          )}
        </div>
      </div>

      {!log && (
        <div className="bg-stone-50 border border-dashed border-stone-300 rounded-sm p-10 text-center text-stone-500">
          {lb.no_log}
        </div>
      )}

      {log && (
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-stone-200 bg-stone-50">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-mono text-stone-600">{lb.version} {log.version}</span>
              <Badge
                data-testid="dl-status-badge"
                variant={log.status === 'active' ? 'default' : 'secondary'}
                className={
                  log.status === 'active' ? 'bg-emerald-700' :
                  log.status === 'superseded' ? 'bg-stone-400' : 'bg-amber-500'
                }
              >
                {log.status === 'active' ? lb.status_active : log.status === 'superseded' ? lb.status_superseded : lb.status_draft}
              </Badge>
              {log.activated_at && (
                <span className="text-xs text-stone-500">{lb.activated_by}: <b>{log.activated_by_email}</b> · {(log.activated_at || '').slice(0, 16).replace('T', ' ')}</span>
              )}
            </div>
            {!isLocked && (
              <Button data-testid="dl-add-entry-btn" size="sm" onClick={() => setEntryDialog({ mode: 'create' })} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2">
                <Plus weight="bold" size={12} /> {lb.add_entry}
              </Button>
            )}
          </div>

          {isLocked && (
            <div className="px-4 py-2 bg-emerald-50 text-emerald-800 text-xs border-b border-emerald-100" data-testid="dl-locked-notice">
              {lb.locked}
            </div>
          )}

          {(!log.entries || log.entries.length === 0) ? (
            <div className="p-8 text-center text-stone-400 text-sm">{lb.no_entries}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="dl-entries-table">
                <thead className="bg-stone-100 text-stone-700">
                  <tr>
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left">{lb.th_name}</th>
                    <th className="px-2 py-2 text-left">{lb.th_role}</th>
                    <th className="px-2 py-2 text-left">{lb.th_qual}</th>
                    <th className="px-2 py-2 text-left">{lb.th_tasks}</th>
                    <th className="px-2 py-2 text-left">{lb.th_from}</th>
                    <th className="px-2 py-2 text-left">{lb.th_to}</th>
                    {!isLocked && <th className="px-2 py-2 text-right">{lb.th_actions}</th>}
                  </tr>
                </thead>
                <tbody>
                  {log.entries.map((e, idx) => (
                    <tr key={e.entry_id} className="border-t border-stone-100 align-top">
                      <td className="px-2 py-2 font-mono text-stone-500">{idx + 1}</td>
                      <td className="px-2 py-2 font-semibold text-stone-800">{e.name}</td>
                      <td className="px-2 py-2 text-stone-600">{e.role_title || '—'}</td>
                      <td className="px-2 py-2 text-stone-600">{e.qualifications || '—'}</td>
                      <td className="px-2 py-2 text-stone-700">
                        <ul className="space-y-0.5">
                          {(e.tasks || []).map(tid => {
                            const t = tasks.find(x => x.task_id === tid);
                            return <li key={tid}>• {t ? t.label_es : tid}</li>;
                          })}
                        </ul>
                      </td>
                      <td className="px-2 py-2 font-mono text-stone-600">{(e.delegated_from || '').slice(0, 10) || '—'}</td>
                      <td className="px-2 py-2 font-mono text-stone-600">{(e.delegated_to || '').slice(0, 10) || (lang === 'es' ? 'En vigor' : 'Ongoing')}</td>
                      {!isLocked && (
                        <td className="px-2 py-2">
                          <div className="flex gap-1 justify-end">
                            <Button data-testid={`dl-edit-${e.entry_id}`} variant="ghost" size="sm" onClick={() => setEntryDialog({ mode: 'edit', entry: e })}>
                              <Pencil size={14} />
                            </Button>
                            <Button data-testid={`dl-delete-${e.entry_id}`} variant="ghost" size="sm" onClick={() => handleDeleteEntry(e)}>
                              <Trash size={14} className="text-red-600" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Historial */}
      {history.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-sm">
          <div className="px-4 py-2 border-b border-stone-200 bg-stone-50 text-xs font-semibold uppercase text-stone-600 tracking-wider">
            {lb.history_title}
          </div>
          <ul className="divide-y divide-stone-100">
            {history.map(h => (
              <li key={h.delegation_id} className="flex items-center justify-between px-4 py-2 text-xs">
                <div>
                  <span className="font-mono text-stone-500">v{h.version}</span>
                  <span className="ml-3 text-stone-600">{(h.activated_at || '').slice(0, 16).replace('T', ' ')} · {h.activated_by_email}</span>
                  <span className="ml-3 text-stone-400 italic">→ {lb.status_superseded.toLowerCase()} {(h.superseded_at || '').slice(0, 10)}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(h.delegation_id)} data-testid={`dl-history-pdf-${h.delegation_id}`}>
                  <FileText size={14} />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {entryDialog && (
        <EntryDialog
          mode={entryDialog.mode}
          entry={entryDialog.entry}
          tasks={tasks}
          users={users}
          lb={lb}
          studyId={studyId}
          onClose={() => setEntryDialog(null)}
          onSaved={() => { setEntryDialog(null); loadAll(); }}
        />
      )}

      <Dialog open={showSign} onOpenChange={(o) => !o && setShowSign(false)}>
        <DialogContent className="max-w-md" data-testid="dl-sign-dialog">
          <DialogHeader>
            <DialogTitle>{lb.sign_dialog_title}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-600">{lb.sign_note}</p>
          <div className="space-y-2">
            <Label className="text-xs">{lb.effective_to}</Label>
            <Input data-testid="dl-effective-to" type="date" value={effectiveTo} onChange={e => setEffectiveTo(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSign(false)}>{lb.cancel}</Button>
            <Button
              data-testid="dl-sign-continue"
              onClick={() => { setShowSign(false); setShowESignDialog(true); }}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >{lb.sign_btn}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ESignature
        open={showESignDialog}
        onClose={() => setShowESignDialog(false)}
        action="SIGN_DELEGATION_LOG"
        resource={`delegation_log:${log?.delegation_id || ''}`}
        onSigned={handleSign}
        lang={lang}
      />
    </div>
  );
}

function EntryDialog({ mode, entry, tasks, users, lb, studyId, onClose, onSaved }) {
  const [form, setForm] = useState(() => entry ? {
    user_id: entry.user_id || '',
    name: entry.name || '',
    role_title: entry.role_title || '',
    qualifications: entry.qualifications || '',
    tasks: entry.tasks || [],
    delegated_from: (entry.delegated_from || '').slice(0, 10),
    delegated_to: (entry.delegated_to || '').slice(0, 10),
  } : {
    user_id: '', name: '', role_title: '', qualifications: '',
    tasks: [], delegated_from: new Date().toISOString().slice(0, 10), delegated_to: '',
  });
  const [saving, setSaving] = useState(false);

  const categorizedTasks = useMemo(() => {
    const groups = {};
    for (const t of tasks) {
      const c = t.category || 'other';
      groups[c] = groups[c] || [];
      groups[c].push(t);
    }
    return groups;
  }, [tasks]);

  const toggleTask = (taskId) => {
    setForm(f => ({
      ...f,
      tasks: f.tasks.includes(taskId) ? f.tasks.filter(x => x !== taskId) : [...f.tasks, taskId],
    }));
  };

  const pickUser = (uid) => {
    const u = users.find(x => x.id === uid || x._id === uid);
    setForm(f => ({
      ...f,
      user_id: uid,
      name: u ? `${u.name || ''} ${u.surname1 || ''}`.trim() : f.name,
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(lb.err_required_name); return; }
    if (!form.tasks.length) { toast.error(lb.err_required_tasks); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        user_id: form.user_id || null,
        delegated_from: form.delegated_from || null,
        delegated_to: form.delegated_to || null,
      };
      if (mode === 'create') {
        await axios.post(`${API}/api/studies/${studyId}/delegation-log/entries`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/api/studies/${studyId}/delegation-log/entries/${entry.entry_id}`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const categoryLabels = { regulatory: 'Regulatorio', clinical: 'Clínico', data: 'Datos', warehouse: 'Almacén', other: 'Otros' };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dl-entry-dialog">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? lb.add_entry : lb.edit}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{lb.f_user}</Label>
              <Select value={form.user_id || '_none'} onValueChange={v => pickUser(v === '_none' ? '' : v)}>
                <SelectTrigger data-testid="dl-entry-user"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none"><span className="italic text-stone-500">Externo / sin usuario</span></SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id || u._id} value={u.id || u._id}>{`${u.name || ''} ${u.surname1 || ''}`.trim()} — {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lb.f_name} *</Label>
              <Input data-testid="dl-entry-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lb.f_role}</Label>
              <Input data-testid="dl-entry-role" value={form.role_title} onChange={e => setForm(f => ({ ...f, role_title: e.target.value }))} placeholder="Investigador colaborador" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lb.f_qual}</Label>
              <Input data-testid="dl-entry-qual" value={form.qualifications} onChange={e => setForm(f => ({ ...f, qualifications: e.target.value }))} placeholder="Médico, Dermatología" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lb.f_from}</Label>
              <Input data-testid="dl-entry-from" type="date" value={form.delegated_from} onChange={e => setForm(f => ({ ...f, delegated_from: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lb.f_to}</Label>
              <Input data-testid="dl-entry-to" type="date" value={form.delegated_to} onChange={e => setForm(f => ({ ...f, delegated_to: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold">{lb.f_tasks} *</Label>
            <div className="space-y-3 border border-stone-200 rounded-sm p-3 bg-stone-50">
              {Object.entries(categorizedTasks).map(([cat, list]) => (
                <div key={cat}>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">{categoryLabels[cat] || cat}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {list.map(t => (
                      <label key={t.task_id} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-white p-1 rounded">
                        <Checkbox
                          data-testid={`dl-entry-task-${t.task_id}`}
                          checked={form.tasks.includes(t.task_id)}
                          onCheckedChange={() => toggleTask(t.task_id)}
                          className="mt-0.5"
                        />
                        <span>{t.label_es}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lb.cancel}</Button>
          <Button data-testid="dl-entry-save" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{lb.save}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
